/**
 * CockpitLayout — Three-pane shell for the Work Inbox cockpit.
 *
 * PRESENTATIONAL only: all state (items, selection, filter, loading)
 * comes in via props. This component does NOT fetch data.
 *
 * Layout (≥1280px):
 *   [TakeawayBar]
 *   [SummaryTiles]
 *   [DealListPane | InboxPreviewRouter | InspectorPaneSlot]
 *
 * The preview pane routes every work_type to its matching detail renderer
 * (WI-INBOX-ROUTING-001 Stage 1). The inspector slot renders DealInspectorPane
 * for deal-type items and a "details available in preview" hint otherwise.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 2 — CockpitLayout spec
 * Design tokens: DS_COLORS, DS_TYPOGRAPHY, DS_SPACING, DS_BORDERS from ui-invariants.js
 */

import React, { useState } from 'react';
import { DS_COLORS, DS_TYPOGRAPHY, DS_SPACING } from '@/config/ui-invariants';
import { COCKPIT_LABELS } from '@/config/task-display-config';
import { Skeleton } from '@/components/ui/skeleton';
import useAnnouncer from '@/hooks/useAnnouncer';
import { Button } from '@/components/ui/button';

import TakeawayBar from './TakeawayBar';
import SummaryTiles from './SummaryTiles';
import DealListPane from './DealListPane';
import InboxPreviewRouter from './InboxPreviewRouter';
import InspectorDrawer from './InspectorDrawer';
import { InspectorPaneSlot } from './InspectorPaneSlot';
import { useCockpitBreakpoint } from './useCockpitBreakpoint';

// ─── Skeleton placeholder for a single pane during initial load ──────────────

function PaneSkeleton({ style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, ...style }}>
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
    </div>
  );
}

// ─── Vertical divider between panes ──────────────────────────────────────────

function PaneDivider() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 1,
        alignSelf: 'stretch',
        backgroundColor: DS_COLORS.border.default,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {Array}        props.items         — pre-loaded inbox items
 * @param {object}       props.summary       — KPI summary object (7 buckets)
 * @param {string|null}  props.summaryError  — set when the summary could not be
 *   loaded. Kept SEPARATE from `error` on purpose: the inbox list and the
 *   summary row come from two different endpoints, and a dead summary must
 *   neither blank the list nor be rendered as seven zeros.
 * @param {object|null}  props.selected      — currently selected InboxItem or null
 * @param {function}     props.onSelect      — (id: string) => void
 * @param {string|null}  props.filterTile    — active tile filter key or null
 * @param {function}     props.onFilterTile  — (id: string|null) => void
 * @param {boolean}      props.loading       — true during initial load
 * @param {string|null}  props.error         — error message or null
 * @param {function}     props.onRefresh     — retry handler
 * @param {string}       props.takeawayText  — italic sentence for TakeawayBar
 */
export default function CockpitLayout({
  items,
  summary,
  summaryError = null,
  selected,
  onSelect,
  filterTile,
  onFilterTile,
  loading,
  error,
  onRefresh,
  takeawayText,
  onAccept,
  onReject,
  onArticleAction,
  onPassDeal,
  onContacted,
}) {
  const bp = useCockpitBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Leg 5 follow-up (inbox announcer gap): a persistent, page-level polite
  // live region. Rendered OUTSIDE every breakpoint-conditional pane block so
  // it survives a successful Pass/Contacted — which removes the acted-on
  // item from `items` and changes `selected.id`, unmounting InboxPreviewRouter's
  // keyed subtree (and any detail renderer's own local live region, e.g.
  // DealCard's) in the same commit the success text would have appeared in.
  // Threaded down via onAnnounce: CockpitLayout -> InboxPreviewRouter ->
  // any detail renderer that accepts it (DealCard already does). Generic —
  // every inbox work-item type gets this for free, not just Tracxn deals.
  // WI-POST-BUTTON-REGRESSION-HUNT-001 — via useAnnouncer, not a bare setState:
  // a repeated identical outcome (Pass twice, the same error twice) is a React
  // no-op and would never be spoken a second time. See the hook for the full why.
  const [statusMessage, announce] = useAnnouncer();

  // Narrow-tablet / mobile: whether the full-screen preview overlay is showing.
  const showMobileOverlay = (bp === 'narrow-tablet' || bp === 'mobile') && selected !== null;

  return (
    <div
      data-testid="accrezeo-cockpit"
      data-active-filter={filterTile || 'all'}
      role="region"
      aria-label={COCKPIT_LABELS.aria.cockpit}
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: DS_COLORS.surface.slate,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 40px', // py-6 px-10
        boxSizing: 'border-box',
      }}
    >
      {/* ── Persistent page-level announcer — see the statusMessage/announce
          declaration above for why this must live here, not inside a pane. */}
      <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

      {/* ── TakeawayBar ──────────────────────────────────────────────── */}
      <TakeawayBar text={takeawayText} />

      {/* ── SummaryTiles ─────────────────────────────────────────────── */}
      <SummaryTiles
        summary={summary}
        summaryError={summaryError}
        filterTile={filterTile}
        onFilterTile={onFilterTile}
        loading={loading}
      />

      {/* ── Error bar ────────────────────────────────────────────────── */}
      {error && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: DS_SPACING.cardGap,
            padding: '10px 16px',
            borderRadius: 6,
            backgroundColor: DS_COLORS.error.background,
          }}
        >
          <span
            style={{
              ...DS_TYPOGRAPHY.bodyMd,
              color: DS_COLORS.text.error,
              flex: 1,
            }}
          >
            {error}
          </span>
          <button
            onClick={onRefresh}
            style={{
              ...DS_TYPOGRAPHY.actionBtn,
              color: DS_COLORS.text.error,
              background: 'transparent',
              border: `1px solid ${DS_COLORS.text.error}`,
              borderRadius: 6,
              padding: '8px 16px',
              minHeight: DS_SPACING.minTouchTarget,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Content area — responsive by breakpoint ──────────────────── */}

      {/* ─────────────────────────────────────────────────────────────────
          DESKTOP (≥1280px): Three panes side-by-side (unchanged).
          TABLET (960-1279px): List + Preview; Inspector in slide-over drawer.
          NARROW-TABLET / MOBILE (<960px): List only; overlay when selected.
         ───────────────────────────────────────────────────────────────── */}

      {/* ── Desktop: three-pane row ──────────────────────────────────── */}
      {bp === 'desktop' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            marginTop: DS_SPACING.cardGap,
            backgroundColor: DS_COLORS.surface.white,
            borderRadius: 8,
            overflow: 'hidden',
            border: `1px solid ${DS_COLORS.border.default}`,
            boxShadow: DS_COLORS.shadow.card,
            minHeight: 0,
          }}
        >
          {/* ── List Pane — 360px fixed ──────────────────────────────── */}
          <div
            data-pane="list"
            style={{
              width: 360,
              flexShrink: 0,
              overflowY: 'auto',
              backgroundColor: DS_COLORS.surface.white,
            }}
          >
            {loading ? (
              <PaneSkeleton />
            ) : (
              <DealListPane
                items={items}
                selectedId={selected?.id ?? null}
                onSelect={onSelect}
                filterTile={filterTile}
              />
            )}
          </div>

          <PaneDivider />

          {/* ── Preview Pane — flex, min 480px ───────────────────────── */}
          <div
            data-pane="preview"
            style={{
              flex: 1,
              minWidth: 480,
              overflowY: 'auto',
              backgroundColor: DS_COLORS.surface.white,
            }}
          >
            {loading ? (
              <PaneSkeleton style={{ padding: 24 }} />
            ) : (
              <InboxPreviewRouter
                selected={selected}
                onSelect={onSelect}
                onAccept={onAccept}
                onReject={onReject}
                onArticleAction={onArticleAction}
                onPassDeal={onPassDeal}
                onContacted={onContacted}
                onRefresh={onRefresh}
                onAnnounce={announce}
              />
            )}
          </div>

          <PaneDivider />

          {/* ── Inspector Pane — 320px fixed ─────────────────────────── */}
          <div
            data-pane="inspector"
            style={{
              width: 320,
              flexShrink: 0,
              overflowY: 'auto',
              backgroundColor: DS_COLORS.surface.white,
            }}
          >
            {loading ? (
              <PaneSkeleton />
            ) : (
              <InspectorPaneSlot selected={selected} onRefresh={onRefresh} />
            )}
          </div>
        </div>
      )}

      {/* ── Tablet (960-1279px): two-pane row + Inspector as slide-over ── */}
      {bp === 'tablet' && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              marginTop: DS_SPACING.cardGap,
              backgroundColor: DS_COLORS.surface.white,
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${DS_COLORS.border.default}`,
              boxShadow: DS_COLORS.shadow.card,
              minHeight: 0,
            }}
          >
            {/* ── List Pane — 360px fixed ──────────────────────────── */}
            <div
              data-pane="list"
              style={{
                width: 360,
                flexShrink: 0,
                overflowY: 'auto',
                backgroundColor: DS_COLORS.surface.white,
              }}
            >
              {loading ? (
                <PaneSkeleton />
              ) : (
                <DealListPane
                  items={items}
                  selectedId={selected?.id ?? null}
                  onSelect={onSelect}
                  filterTile={filterTile}
                />
              )}
            </div>

            <PaneDivider />

            {/* ── Preview Pane — flex; triggers Inspector drawer ────── */}
            <div
              data-pane="preview"
              style={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                backgroundColor: DS_COLORS.surface.white,
              }}
            >
              {loading ? (
                <PaneSkeleton style={{ padding: 24 }} />
              ) : (
                <InboxPreviewRouter
                  selected={selected}
                  onSelect={onSelect}
                  onOpenInspector={() => setDrawerOpen(true)}
                  onAccept={onAccept}
                  onReject={onReject}
                  onArticleAction={onArticleAction}
                  onPassDeal={onPassDeal}
                onContacted={onContacted}
                  onRefresh={onRefresh}
                  onAnnounce={announce}
                />
              )}
            </div>
          </div>

          {/* Inspector as slide-over drawer (outside the row) */}
          <InspectorDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            selected={selected}
            onRefresh={onRefresh}
          />
        </>
      )}

      {/* ── Narrow-tablet / mobile (<960px): list only, overlay on select ── */}
      {(bp === 'narrow-tablet' || bp === 'mobile') && (
        <>
          {/* List pane — full width */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              marginTop: DS_SPACING.cardGap,
              backgroundColor: DS_COLORS.surface.white,
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${DS_COLORS.border.default}`,
              boxShadow: DS_COLORS.shadow.card,
              minHeight: 0,
            }}
          >
            <div
              data-pane="list"
              style={{
                flex: 1,
                overflowY: 'auto',
                backgroundColor: DS_COLORS.surface.white,
              }}
            >
              {loading ? (
                <PaneSkeleton />
              ) : (
                <DealListPane
                  items={items}
                  selectedId={selected?.id ?? null}
                  onSelect={onSelect}
                  filterTile={filterTile}
                />
              )}
            </div>
          </div>

          {/* Full-screen overlay when a row is selected */}
          {showMobileOverlay && (
            <div
              data-cockpit-mobile-overlay="true"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 50,
                backgroundColor: DS_COLORS.surface.white,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Back button */}
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${DS_COLORS.border.default}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="ghost"
                  onClick={() => onSelect(null)}
                  style={{ minHeight: 44 }}
                >
                  {COCKPIT_LABELS.overlay.back}
                </Button>
              </div>

              {/* Preview pane */}
              <div
                data-pane="preview"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  backgroundColor: DS_COLORS.surface.white,
                }}
              >
                <InboxPreviewRouter
                  selected={selected}
                  onSelect={onSelect}
                  onOpenInspector={() => setDrawerOpen(true)}
                  onAccept={onAccept}
                  onReject={onReject}
                  onArticleAction={onArticleAction}
                  onPassDeal={onPassDeal}
                onContacted={onContacted}
                  onRefresh={onRefresh}
                  onAnnounce={announce}
                />
              </div>
            </div>
          )}

          {/* Inspector slide-over drawer (accessible from overlay) */}
          <InspectorDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            selected={selected}
            onRefresh={onRefresh}
          />
        </>
      )}
    </div>
  );
}
